import "./index.css";

import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { setupIonicReact } from "@ionic/react";
import { QueryClientProvider } from "@tanstack/react-query";
import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { hasValue, isNullish } from "@core/foundation/types";

import { relayConsole } from "@react/foundation/debug";
import { LoggerProvider } from "@react/foundation/logger";
import { createQueryClient } from "@react/foundation/query";
import { handleDeepLinks } from "@react/ionic/deep-link";
import { Toaster } from "@react/ui-primitives/sonner";

import { isUserToken } from "@platform/core";

import { App } from "./App.tsx";
import { deviceStorage } from "./bootstrap/device-storage.ts";
import { logger } from "./bootstrap/logger.ts";
import {
  authStore,
  initConfig,
  signinOnReturn,
} from "./features/auth/index.ts";

if (import.meta.hot) {
  relayConsole(import.meta.hot);
}

setupIonicReact();

const queryClient = createQueryClient(logger);

// oxlint-disable-next-line typescript/no-floating-promises
Promise.all([
  defineCustomElements(window),
  deviceStorage.connect(),
  signin(window.location.href),
  render(),
]);

async function render(): Promise<void> {
  const { authToken } = authStore;

  if (hasValue(authToken) && isUserToken(authToken)) {
    await initConfig({ authToken });
  }

  const rootElement = document.querySelector("#root");

  if (isNullish(rootElement)) {
    throw new Error("Root element not found");
  }

  createRoot(rootElement).render(
    <StrictMode>
      <LoggerProvider logger={logger}>
        <QueryClientProvider client={queryClient}>
          <App />
          <Toaster
            richColors
            style={{
              transform: "translateY(calc(-1 * var(--safe-bottom, 0px) + 8px))",
            }}
          />
        </QueryClientProvider>
      </LoggerProvider>
    </StrictMode>,
  );
}

async function signin(url: string): Promise<void> {
  await signinOnReturn(url);
  await handleDeepLinks([signinOnReturn]);
}
